import React from 'react';
import type { PendingAttachment } from '../../constants/fileUploadConstants';

export interface AttachmentChipProps {
  attachment: PendingAttachment;
  onRemove: (localId: string) => void;
  onRetry: (localId: string) => void;
}

/** Document glyph shown on the left of every chip. */
const ICN_FILE = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

const ICN_RETRY = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M23 4v6h-6" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const ICN_CLOSE = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

/** Border/background tint per status. */
const STATUS_TINT: Record<string, string> = {
  pending: 'border-border-default bg-surface',
  creating: 'border-border-default bg-surface',
  uploading: 'border-esa-accent/40 bg-esa-accent-muted',
  ready: 'border-border-default bg-surface',
  failed: 'border-error/40 bg-error/5',
};

export const AttachmentChip: React.FC<AttachmentChipProps> = ({ attachment, onRemove, onRetry }) => {
  const { localId, fileName, status, progress, error } = attachment;

  // Allow remove in all states except 'creating' (metadata request in flight).
  // During 'uploading', removing aborts the XHR via useFileUpload.handleRemove.
  const canRemove = status !== 'creating';
  const showRetry = status === 'failed';
  const showProgress = status === 'uploading';
  const isBusy = status === 'pending' || status === 'creating';

  return (
    <div
      className={`group inline-flex items-center gap-2 max-w-[220px] rounded-lg border px-2.5 py-1.5 text-xs ${STATUS_TINT[status] ?? STATUS_TINT.pending}`}
      role="listitem"
      aria-label={`Attachment: ${fileName}, status: ${status}`}
    >
      <span className="shrink-0 text-text-tertiary" aria-hidden>
        {ICN_FILE}
      </span>

      <div className="flex min-w-0 flex-col">
        <span className="truncate font-medium text-text-primary" title={fileName}>
          {fileName}
        </span>

        {showProgress && (
          <span className="mt-1 flex items-center gap-1.5">
            <span className="h-1 w-16 overflow-hidden rounded-full bg-border-default" aria-hidden>
              <span
                className="block h-full rounded-full bg-esa-accent transition-[width] duration-150"
                style={{ width: `${progress}%` }}
              />
            </span>
            <span
              className="text-[10px] tabular-nums text-text-secondary"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Upload progress: ${progress}%`}
            >
              {progress}%
            </span>
          </span>
        )}

        {isBusy && (
          <span className="mt-0.5 text-[10px] text-text-tertiary">Preparing…</span>
        )}

        {error && (
          <span className="mt-0.5 truncate text-[10px] text-error" role="alert" title={error}>
            {error}
          </span>
        )}
      </div>

      {status === 'ready' && (
        <span className="shrink-0 text-success" aria-hidden title="Uploaded">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
      )}

      {showRetry && (
        <button
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-esa-accent hover:bg-esa-accent-muted"
          type="button"
          aria-label="Retry upload"
          onClick={() => onRetry(localId)}
        >
          {ICN_RETRY}
          Retry
        </button>
      )}

      {canRemove && (
        <button
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-text-tertiary hover:bg-surface-hover hover:text-text-primary"
          type="button"
          aria-label="Remove attachment"
          onClick={() => onRemove(localId)}
        >
          {ICN_CLOSE}
        </button>
      )}
    </div>
  );
};
